/**
 * QuotaManager - Central service for managing usage limits in local mode.
 *
 * All quota operations go through this service to ensure consistency.
 * Used by stores and components to check/enforce limits.
 */

import { save, load } from '../local/storage';
import type { QuotaState } from '@/types/quota';
import type { Vacancy } from '@/services/vacancy.service';

const QUOTA_KEY = 'quota';

export class QuotaManager {
  /**
   * Load current quota state from localStorage.
   * Returns default quota if none exists.
   */
  static getQuota(): QuotaState {
    const defaultQuota: QuotaState = {
      cvGenerations: { count: 0, limit: 5, generatedIds: [] },
      vacancySaves: { count: 0, limit: 10, vacancies: [] },
      coverLetters: { count: 0, limit: 2, letterIds: [] },
      version: 1,
    };

    return load<QuotaState>(QUOTA_KEY, defaultQuota);
  }

  /**
   * Save quota state to localStorage.
   * Private method - use specific increment/add methods instead.
   */
  private static saveQuota(quota: QuotaState): void {
    save(QUOTA_KEY, quota);
  }

  // ============================================
  // CV Generation Quota
  // ============================================

  /**
   * Check if user can generate another CV.
   * Returns false if limit reached (5/5).
   */
  static canGenerateCV(): boolean {
    const quota = this.getQuota();
    return quota.cvGenerations.count < quota.cvGenerations.limit;
  }

  /**
   * Increment CV generation count.
   * Call this AFTER successful CV generation.
   *
   * @param cvId - ID of generated CV (for tracking)
   * @throws Error if limit already reached (should check canGenerateCV first)
   */
  static incrementCVGeneration(cvId: string): void {
    const quota = this.getQuota();

    if (quota.cvGenerations.count >= quota.cvGenerations.limit) {
      throw new Error('CV generation limit reached');
    }

    quota.cvGenerations.count++;
    quota.cvGenerations.generatedIds.push(cvId);
    this.saveQuota(quota);
  }

  /**
   * Get remaining free CV generations.
   * Returns 0-5.
   */
  static getRemainingCVs(): number {
    const quota = this.getQuota();
    return Math.max(0, quota.cvGenerations.limit - quota.cvGenerations.count);
  }

  /**
   * Get CV generation progress as percentage.
   * Returns 0-100.
   */
  static getCVProgress(): number {
    const quota = this.getQuota();
    return (quota.cvGenerations.count / quota.cvGenerations.limit) * 100;
  }

  // ============================================
  // Vacancy Save Quota (FIFO)
  // ============================================

  /**
   * Check if user can save another vacancy without FIFO removal.
   * Returns false if at 10/10 limit.
   */
  static canSaveVacancy(): boolean {
    const quota = this.getQuota();
    return quota.vacancySaves.count < quota.vacancySaves.limit;
  }

  /**
   * Add a vacancy to saved list.
   * If at limit (10/10), removes oldest vacancy (FIFO).
   *
   * @param vacancy - Vacancy to add
   * @returns Object with: { added: Vacancy, removed?: Vacancy }
   */
  static addVacancy(vacancy: Vacancy): { added: Vacancy; removed?: Vacancy } {
    const quota = this.getQuota();
    let removed: Vacancy | undefined;

    // FIFO: remove oldest if at limit
    if (quota.vacancySaves.vacancies.length >= quota.vacancySaves.limit) {
      removed = quota.vacancySaves.vacancies.shift(); // Remove first (oldest)
    }

    // Add new vacancy to end (newest)
    quota.vacancySaves.vacancies.push(vacancy);
    quota.vacancySaves.count = quota.vacancySaves.vacancies.length;

    this.saveQuota(quota);

    return { added: vacancy, removed };
  }

  /**
   * Remove a specific vacancy by ID.
   * Used when user manually deletes a saved job.
   *
   * @param vacancyId - ID of vacancy to remove
   * @returns true if removed, false if not found
   */
  static removeVacancy(vacancyId: string): boolean {
    const quota = this.getQuota();
    const initialLength = quota.vacancySaves.vacancies.length;

    quota.vacancySaves.vacancies = quota.vacancySaves.vacancies.filter(
      (v) => v.id !== vacancyId
    );

    const removed = quota.vacancySaves.vacancies.length < initialLength;

    if (removed) {
      quota.vacancySaves.count = quota.vacancySaves.vacancies.length;
      this.saveQuota(quota);
    }

    return removed;
  }

  /**
   * Get all saved vacancies.
   * Returns array ordered by save time (oldest first).
   */
  static getSavedVacancies(): Vacancy[] {
    const quota = this.getQuota();
    return quota.vacancySaves.vacancies;
  }

  /**
   * Get vacancy that will be removed on next save (oldest).
   * Returns null if under limit.
   */
  static getNextRemovalCandidate(): Vacancy | null {
    const quota = this.getQuota();
    if (quota.vacancySaves.count < quota.vacancySaves.limit) {
      return null;
    }
    return quota.vacancySaves.vacancies[0] || null;
  }

  // ============================================
  // Cover Letter Quota
  // ============================================

  /**
   * Check if user can generate another cover letter.
   * Note: This is a SOFT limit (user can still generate, just shows prompt).
   */
  static canGenerateCoverLetter(): boolean {
    const quota = this.getQuota();
    return quota.coverLetters.count < quota.coverLetters.limit;
  }

  /**
   * Increment cover letter generation count.
   *
   * @param letterId - ID of generated cover letter
   */
  static incrementCoverLetter(letterId: string): void {
    const quota = this.getQuota();
    quota.coverLetters.count++;
    quota.coverLetters.letterIds.push(letterId);
    this.saveQuota(quota);
  }

  /**
   * Get remaining free cover letter generations.
   */
  static getRemainingCoverLetters(): number {
    const quota = this.getQuota();
    return Math.max(0, quota.coverLetters.limit - quota.coverLetters.count);
  }

  // ============================================
  // Admin / Reset
  // ============================================

  /**
   * Reset all quotas to unlimited.
   * Called when user registers (switches to cloud mode).
   */
  static resetQuota(): void {
    const defaultQuota: QuotaState = {
      cvGenerations: { count: 0, limit: 999999, generatedIds: [] },
      vacancySaves: { count: 0, limit: 999999, vacancies: [] },
      coverLetters: { count: 0, limit: 999999, letterIds: [] },
      version: 1,
    };
    this.saveQuota(defaultQuota);
  }

  /**
   * Get full quota summary for display.
   * Used by dashboard and settings pages.
   */
  static getQuotaSummary() {
    const quota = this.getQuota();
    return {
      cvs: {
        used: quota.cvGenerations.count,
        limit: quota.cvGenerations.limit,
        remaining: this.getRemainingCVs(),
        percentage: this.getCVProgress(),
      },
      vacancies: {
        used: quota.vacancySaves.count,
        limit: quota.vacancySaves.limit,
        remaining: quota.vacancySaves.limit - quota.vacancySaves.count,
        atLimit: quota.vacancySaves.count >= quota.vacancySaves.limit,
      },
      coverLetters: {
        used: quota.coverLetters.count,
        limit: quota.coverLetters.limit,
        remaining: this.getRemainingCoverLetters(),
      },
    };
  }

  /**
   * Export quota data for migration to cloud.
   * Returns serializable object for POST to backend.
   */
  static exportForMigration() {
    const quota = this.getQuota();
    return {
      cvGenerationCount: quota.cvGenerations.count,
      savedVacancies: quota.vacancySaves.vacancies,
      coverLetterCount: quota.coverLetters.count,
    };
  }
}
